import { useEffect, useMemo, useState } from "react";
import { MapPin, Flame, Layers } from "lucide-react";
import api from "@/lib/api";
import { WorldMap } from "@/components/WorldMap";
import { PHASES } from "@/lib/phases";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function MapPage() {
  const [points, setPoints] = useState([]);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("all");
  const [enabled, setEnabled] = useState(
    Object.fromEntries(PHASES.map((p) => [p.key, true]))
  );
  const [mode, setMode] = useState("pins");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const params = showAll ? { stubs: "show" } : {};
    api.get("/sites/geo", { params }).then((r) => setPoints(r.data)).catch(() => {});
    api.get("/countries", { params }).then((r) => setCountries(r.data)).catch(() => {});
  }, [showAll]);

  const counts = useMemo(() => {
    const c = Object.fromEntries(PHASES.map((p) => [p.key, 0]));
    points.forEach((p) => { c[p.phase] = (c[p.phase] || 0) + 1; });
    return c;
  }, [points]);

  const visible = useMemo(
    () => points.filter((p) => enabled[p.phase] && (country === "all" || p.country === country)),
    [points, enabled, country]
  );

  const toggle = (k) => setEnabled((e) => ({ ...e, [k]: !e[k] }));

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-8">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-2 flex items-center gap-2">
        <MapPin className="h-3.5 w-3.5" /> Global Distribution
      </p>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground">Interactive Map</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Every documented entry across all three areas — Structures, Religion and Language — pinned to its
        place of origin — every site shown as its own pin. Toggle categories, filter by country, and select any pin.
      </p>

      {/* Phase toggles */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {PHASES.map((ph) => {
          const on = enabled[ph.key];
          return (
            <button
              key={ph.key}
              data-testid={`phase-toggle-${ph.key}`}
              onClick={() => toggle(ph.key)}
              className="group flex items-center gap-2.5 border px-4 py-2.5 font-mono text-xs uppercase tracking-wider transition-colors"
              style={{
                borderColor: on ? ph.color : "hsl(var(--border))",
                background: on ? ph.color : "transparent",
                color: on ? "#fff" : "hsl(var(--muted-foreground))",
              }}
            >
              <span className="h-2.5 w-2.5 rounded-full border" style={{ background: on ? "#fff" : ph.color, borderColor: on ? "#fff" : ph.color }} />
              <span>{ph.roman ? `${ph.roman} · ${ph.label}` : ph.label}</span>
              <span className="opacity-70">{counts[ph.key] || 0}</span>
            </button>
          );
        })}

        <div className="ml-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <button
            data-testid="map-show-all"
            onClick={() => setShowAll((v) => !v)}
            title="Include auto-catalogued stub sites (thin records, no deep-dive yet)"
            className="flex items-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-wider border transition-colors"
            style={{
              borderColor: showAll ? "hsl(var(--foreground))" : "hsl(var(--border))",
              background: showAll ? "hsl(var(--foreground))" : "transparent",
              color: showAll ? "hsl(var(--background))" : "hsl(var(--muted-foreground))",
            }}
          >
            <Layers className="h-3.5 w-3.5" /> {showAll ? "Full Catalogue" : "Curated Only"}
          </button>
          <div className="flex border border-border" data-testid="map-view-toggle">
            <button
              data-testid="map-view-pins"
              onClick={() => setMode("pins")}
              className="flex items-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-wider transition-colors"
              style={{
                background: mode === "pins" ? "hsl(var(--foreground))" : "transparent",
                color: mode === "pins" ? "hsl(var(--background))" : "hsl(var(--muted-foreground))",
              }}
            >
              <MapPin className="h-3.5 w-3.5" /> Pins
            </button>
            <button
              data-testid="map-view-heat"
              onClick={() => setMode("heat")}
              className="flex items-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-wider transition-colors border-l border-border"
              style={{
                background: mode === "heat" ? "#9A3412" : "transparent",
                color: mode === "heat" ? "#fff" : "hsl(var(--muted-foreground))",
              }}
            >
              <Flame className="h-3.5 w-3.5" /> Heatmap
            </button>
          </div>

          <div className="w-full sm:w-64">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="map-country-select" className="font-mono text-xs uppercase tracking-wider">
                <SelectValue placeholder="Filter by country" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <WorldMap points={visible} height="72vh" mode={mode} />
      </div>
      <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
        Showing {visible.length.toLocaleString()} of {points.length.toLocaleString()} documented sites
        {country !== "all" ? ` · ${country}` : ""}
        {mode === "heat" ? " · density view" : ""}
      </p>
    </div>
  );
}
